import React from "react";
import { Button, Flex, ModalV2, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";

export type ApprovePackageConfirmModalProps = {
  isOpen: boolean;
  documentCount: number;
  packageCount: number;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
};

/**
 * ApprovePackageConfirmModal — confirms the terminal Approve action before it
 * creates the deliverable package(s) and starts Fay's review. Mirrors
 * RemoveDocumentConfirmModal's ModalV2 pattern; unlike that one this is a
 * proceed-confirm, not a destructive-delete, so the tone stays neutral.
 */
export const ApprovePackageConfirmModal = ({
  isOpen,
  documentCount,
  packageCount,
  onClose,
  onConfirm,
  loading,
}: ApprovePackageConfirmModalProps) => {
  const docsPhrase = documentCount === 1 ? "this document" : `these ${documentCount} documents`;
  const packagesPhrase =
    packageCount === 1 ? "1 deliverable package" : `${packageCount} deliverable packages`;
  const confirmLabel = packageCount === 1 ? "Create package" : "Create packages";

  return (
    // Every dismissal path closes while the approve write is in flight, not just
    // the Cancel button — otherwise escape or the close button strands the user
    // outside a terminal action they can't tell succeeded.
    <ModalV2
      show={isOpen}
      onHide={onClose}
      closeOnClickOutside={!loading}
      escapeToClose={!loading}
      closeButton={!loading}
      size="small"
      title="Create deliverable package?"
    >
      <ModalV2.Body>
        <Flex flexDirection="column" gap={12}>
          <Text variant="regular-md">
            Submitting {docsPhrase} will create {packagesPhrase} and Fay will begin running its
            review.
          </Text>
          <Text variant="regular-md">
            You won&rsquo;t be able to edit the classification afterward.
          </Text>
          <Text variant="regular-md">
            To fix a mistaken classification, delete the document from within the package and
            re-upload.
          </Text>
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end" gap={Sizes.SM}>
          <Button variant="neutral" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} isLoading={loading}>
            {confirmLabel}
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
